import type { LibraryLocation } from 'generated/graphql';
import type {
    ContactLoaderData,
    LocationDataType,
    LocationStateType,
    ProfessionalInitialType,
} from '../type/contact-page-types';
import type { getLinkedProfiles } from '../api/get-linked-profiles.server';

export const getInitialContactID = (
    urlLocationState: LocationStateType,
    searchParams: URLSearchParams,
): number | null => {
    if (urlLocationState?.ID) {
        return urlLocationState.ID;
    }
    const locationID = searchParams.get('ID');
    if (locationID) {
        return parseInt(locationID);
    }
    return null;
};

export const convertLocationData = (
    location: Partial<LocationDataType | LibraryLocation>,
): LocationDataType => {
    return {
        ID: location.ID || 0,
        Title: location.Title || '',
        Type: location.Type || '',
        Phone: location.Phone || '',
        Address1: location.Address1 || '',
        Address2: location.Address2 || '',
        Suburb: location.Suburb || '',
        City: location.City || '',
        Region: location.Region || '',
        Country: location.Country || '',
        CountryName: location.CountryName || '',
        PostCode: location.PostCode || '',
        MapCoordinates: location.MapCoordinates || {
            Latitude: 0,
            Longitude: 0,
        },
        MemberSortOrder: location.MemberSortOrder || 0,
        AllowsPickup: location.AllowsPickup || false,
    };
};

export const sortLocationsByMemberOrder = (locations: LocationDataType[]) => {
    return [...locations].sort((a, b) => {
        const orderA = a.MemberSortOrder ?? 0;
        const orderB = b.MemberSortOrder ?? 0;
        if (orderB === orderA) {
            return a.ID - b.ID;
        }
        return orderB - orderA;
    });
};

export const getLinkedProfileLocations = (
    professional: Pick<ProfessionalInitialType, 'LibraryLocations'>,
    linkedProfiles: Pick<
        NonNullable<Awaited<ReturnType<typeof getLinkedProfiles>>>,
        'linkedProfilesCount' | 'linkedProfilesList'
    > | null,
): LocationDataType[] => {
    const professionalLocations = professional.LibraryLocations.filter(
        (l) => !l.LocationHidden,
    ).map(convertLocationData);

    if (!linkedProfiles || linkedProfiles.linkedProfilesCount === 0) {
        return sortLocationsByMemberOrder(professionalLocations);
    }

    const linkedProfilesLocations = linkedProfiles.linkedProfilesList.reduce(
        (locations, profile) => {
            const filteredLocations = profile.LibraryLocations.filter(
                (ll) => !ll.LocationHidden,
            );
            for (const location of filteredLocations) {
                if (locations.some((l) => l.ID === location.ID)) {
                    continue;
                }

                locations.push({
                    ...convertLocationData(location),
                    professionalID: profile.ID,
                });
            }
            return locations;
        },
        professionalLocations,
    );

    return sortLocationsByMemberOrder(linkedProfilesLocations);
};

export const getFullAddress = (
    location: LocationDataType,
    withCountry = false,
): string => {
    const addressParts = [
        location.Address1,
        location.Address2,
        location.Suburb,
        location.City,
        location.Region,
        withCountry ? location.Country : undefined,
        location.PostCode,
    ]
        .map((part) => part?.trim() || '')
        .filter(Boolean);

    // Remove consecutive duplicates (case-insensitive), keeping the first occurrence
    const uniqueParts = addressParts.filter((part, index) => {
        if (index === 0) return true;
        return part.toLowerCase() !== addressParts[index - 1]?.toLowerCase();
    });

    return uniqueParts.join(', ');
};

export const getLocationSuburbText = (
    location: LocationDataType | undefined,
    showCountry = false,
) => {
    if (!location) {
        return '';
    }
    const locationArray = [
        location.Suburb,
        location.City || location.Region,
    ].filter(Boolean);
    const suburbText =
        locationArray[0] === locationArray[1]
            ? locationArray[0]
            : locationArray.join(', ');
    if (!suburbText || !showCountry) {
        return suburbText;
    }
    // Named by the server from the country table, so a location in a newly imported country
    // spells itself out without a front-end list to add it to.
    const countryName = location.CountryName;
    if (!countryName) {
        return suburbText;
    }
    if (suburbText.toLowerCase() === countryName.toLowerCase()) {
        return suburbText;
    }
    return `${suburbText}, ${countryName}`;
};

/** Payload stored on LocationReveal `LoggedEvent.ExtraData`. */
export type LocationRevealExtraData = {
    locationId: number;
    city?: string;
    suburb?: string;
};

/**
 * Compact ExtraData for LocationReveal events so emails/analytics can show
 * which showroom was opened (not the visitor's GeoIP city).
 */
export const buildLocationRevealExtraData = (
    location:
        | Pick<LocationDataType, 'ID' | 'City' | 'Suburb'>
        | null
        | undefined,
): LocationRevealExtraData | undefined => {
    if (!location?.ID) {
        return undefined;
    }

    const city = location.City?.trim();
    const suburb = location.Suburb?.trim();

    return {
        locationId: location.ID,
        ...(city ? { city } : {}),
        ...(suburb ? { suburb } : {}),
    };
};

export const scrollToRefTop = (
    ref: React.RefObject<HTMLDivElement | null>,
    offset = 20,
) => {
    if (ref.current) {
        window.scrollTo({
            top: ref.current.offsetTop - offset,
            behavior: 'smooth',
        });
    }
};

export const getLocationProfessional = (
    location: LocationDataType,
    professional: ProfessionalInitialType,
    linkedProfiles?: ContactLoaderData['linkedProfiles'],
) => {
    const locationProfessional =
        location.professionalID && linkedProfiles
            ? linkedProfiles.linkedProfilesList.find(
                  (p) => p.ID === location.professionalID,
              ) || professional
            : professional;

    return { ...locationProfessional, Link: professional.Link };
};
